/* eslint-disable no-unused-vars */
/* MCP connection create/edit form (A-0008.07).
 *
 * Save flow:
 *   (1) For each populated *_ref-target field with a NEW secret value, POST to
 *       /cm/redis/mcp-connections/:id/secret to write the secret to Vault and
 *       receive back a JSON-pointer ref string.
 *   (2) Build the connection's `auth` block with `<field>_ref` set to the
 *       returned ref.
 *   (3) Call createMCPConnection / updateMCPConnection.
 *
 * Edit flow: existing `*_ref` values are retained when the secret input is
 * left empty (i.e. user is not rotating the secret). If user enters a new
 * value, the secret is rewritten and the ref is updated.
 */
import {
    useState
} from 'react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
    mcpConnectionService
} from 'shared/api/services/BEX/mcpConnection.service';
import BexMcpCredentials, {
    BexMcpBearerWarning, errorText
} from './BexMcpCredentials';

const MCP_AUTH_TYPES = ['none', 'bearer', 'api_key', 'oauth2'];

const MCPForm = ({
    mode, orgId, subOrgId, existing,
    draft, onDraftChange, onSaved, onCancel, onTest,
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // New-secret values (write-only — never read from existing). When non-empty
    // on save, we POST to /:id/secret and get back the ref.
    const [newBearer, setNewBearer] = useState('');
    const [newApiKey, setNewApiKey] = useState('');
    const [newOauth2Secret, setNewOauth2Secret] = useState('');

    const set = (patch) => onDraftChange({
        ...draft,
        ...patch
    });
    const setAuth = (patch) => onDraftChange({
        ...draft,
        auth: {
            ...draft.auth,
            ...patch
        }
    });

    const validate = () => {
        if (!draft.connection_name?.trim()) return 'connection_name is required';
        if (!draft.server_url?.trim()) return 'server_url is required';
        try { new URL(draft.server_url); } catch { return 'server_url must be a valid URL'; }
        if (draft.timeout_ms < 1000 || draft.timeout_ms > 120000) return 'timeout_ms must be between 1000 and 120000';
        if (draft.auth.type === 'bearer' && mode === 'create' && !newBearer.trim()) return 'bearer_token required';
        if (draft.auth.type === 'api_key') {
            if (mode === 'create' && !newApiKey.trim()) return 'api_key required';
            if (!draft.auth.api_key_header?.trim()) return 'api_key_header required';
        }
        if (draft.auth.type === 'oauth2') {
            if (mode === 'create' && !newOauth2Secret.trim()) return 'oauth2_client_secret required';
            if (!draft.auth.oauth2_client_id?.trim()) return 'oauth2_client_id required';
            if (!draft.auth.oauth2_token_url?.trim()) return 'oauth2_token_url required';
        }
        return null;
    };

    const handleSave = async () => {
        const v = validate();
        if (v) { setError(v); return; }
        setError('');
        setBusy(true);

        try {
            // For CREATE, we must have a connection_id BEFORE writing secrets,
            // because the Vault path is `mcp.<connection_id>.<field>`. Server
            // generates the id, so on create we must persist FIRST without secrets,
            // then write secrets, then update the doc with the refs. (Two-phase.)
            // For UPDATE, we have the id already.

            let conn;
            const baseDoc = {
                org_id: orgId,
                sub_org_id: subOrgId,
                connection_name: draft.connection_name,
                description: draft.description,
                server_url: draft.server_url,
                transport: draft.transport,
                timeout_ms: Number(draft.timeout_ms),
                headers: draft.headers || {},
                // auth block built below per phase
            };

            if (mode === 'create') {
                // Phase 1: create with auth.type=none (placeholder) so the server
                // assigns a connection_id. Real auth wired in phase 3.
                conn = await mcpConnectionService.createMCPConnection({
                    ...baseDoc,
                    auth: {
                        type: 'none'
                    },
                });
            } else {
                conn = existing;
            }

            const cid = conn.connection_id;

            // Phase 2: write secrets to Vault.
            const newAuth = {
                type: draft.auth.type
            };

            if (draft.auth.type === 'bearer') {
                if (newBearer.trim()) {
                    const {
                        ref
                    } = await mcpConnectionService.writeMCPSecret(cid, {
                        organizationId: orgId,
                        field: 'bearer_token',
                        value: newBearer,
                    });
                    newAuth.bearer_token_ref = ref;
                } else if (mode === 'edit') {
                    newAuth.bearer_token_ref = existing?.auth?.bearer_token_ref;
                }
            } else if (draft.auth.type === 'api_key') {
                if (newApiKey.trim()) {
                    const {
                        ref
                    } = await mcpConnectionService.writeMCPSecret(cid, {
                        organizationId: orgId,
                        field: 'api_key',
                        value: newApiKey,
                    });
                    newAuth.api_key_ref = ref;
                } else if (mode === 'edit') {
                    newAuth.api_key_ref = existing?.auth?.api_key_ref;
                }
                newAuth.api_key_header = draft.auth.api_key_header;
            } else if (draft.auth.type === 'oauth2') {
                if (newOauth2Secret.trim()) {
                    const {
                        ref
                    } = await mcpConnectionService.writeMCPSecret(cid, {
                        organizationId: orgId,
                        field: 'oauth2_client_secret',
                        value: newOauth2Secret,
                    });
                    newAuth.oauth2_client_secret_ref = ref;
                } else if (mode === 'edit') {
                    newAuth.oauth2_client_secret_ref = existing?.auth?.oauth2_client_secret_ref;
                }
                newAuth.oauth2_client_id = draft.auth.oauth2_client_id;
                newAuth.oauth2_token_url = draft.auth.oauth2_token_url;
                if (draft.auth.oauth2_scopes) newAuth.oauth2_scopes = draft.auth.oauth2_scopes;
            }

            // Phase 3: update with the real auth block.
            await mcpConnectionService.updateMCPConnection(cid, {
                ...baseDoc,
                auth: newAuth,
            });

            const savedConnection = {
                connection_id: cid,
                connection_name: baseDoc.connection_name,
                description: baseDoc.description,
                server_url: baseDoc.server_url,
                transport: baseDoc.transport,
                timeout_ms: baseDoc.timeout_ms,
                headers: baseDoc.headers || {},
                auth: newAuth,
                org_id: orgId,
                sub_org_id: subOrgId,
            };

            await onSaved(savedConnection);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 max-w-2xl">
            {/* Internal heading + Test trigger removed — DialogTitle and the
                outer Test toggle handle both responsibilities. Keeping just
                the error surface below. */}
            {error && <p className="text-xs text-destructive">{String(error)}</p>}

            <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                    value={draft.connection_name}
                    onChange={(e) => set({
                        connection_name: e.target.value
                    })}
                    placeholder="e.g. Internal docs MCP"
                />
            </div>

            <div className="space-y-2">
                <Label>Description</Label>
                <Input
                    value={draft.description}
                    onChange={(e) => set({
                        description: e.target.value
                    })}
                    placeholder="(optional)"
                />
            </div>

            <div className="space-y-2">
                <Label>Server URL *</Label>
                <Input
                    value={draft.server_url}
                    onChange={(e) => set({
                        server_url: e.target.value
                    })}
                    placeholder="https://mcp.example.com/v1"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label>Transport</Label>
                    <Select
                        value={draft.transport}
                        onValueChange={(v) => set({
                            transport: v
                        })}
                    >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="streamable_http">streamable_http</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Timeout (ms)</Label>
                    <Input
                        type="number"
                        min={1000}
                        max={120000}
                        value={draft.timeout_ms}
                        onChange={(e) => set({
                            timeout_ms: Number(e.target.value)
                        })}
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label>Auth type</Label>
                <Select
                    value={draft.auth.type}
                    onValueChange={(v) => onDraftChange({
                        ...draft,
                        auth: {
                            type: v
                        }
                    })}
                >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {MCP_AUTH_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {draft.auth.type === 'bearer' && (
                <div className="space-y-2">
                    <Label>Bearer token {mode === 'edit' && existing?.auth?.bearer_token_ref ? '(leave empty to keep existing)' : '*'}</Label>
                    <Input
                        type="password"
                        value={newBearer}
                        onChange={(e) => setNewBearer(e.target.value)}
                        placeholder={mode === 'edit' && existing?.auth?.bearer_token_ref ? '<configured — enter new to rotate>' : 'sk-...'}
                    />
                    <BexMcpBearerWarning serverUrl={draft.server_url} />
                </div>
            )}

            {draft.auth.type === 'api_key' && (
                <>
                    <div className="space-y-2">
                        <Label>API key {mode === 'edit' && existing?.auth?.api_key_ref ? '(leave empty to keep existing)' : '*'}</Label>
                        <Input
                            type="password"
                            value={newApiKey}
                            onChange={(e) => setNewApiKey(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>API key header *</Label>
                        <Input
                            value={draft.auth.api_key_header || ''}
                            onChange={(e) => setAuth({
                                api_key_header: e.target.value
                            })}
                            placeholder="X-Api-Key"
                        />
                    </div>
                </>
            )}

            {draft.auth.type === 'oauth2' && (
                <>
                    <BexMcpCredentials
                        orgId={orgId}
                        subOrgId={subOrgId}
                        draft={draft}
                        onDraftChange={onDraftChange}
                        onSecret={setNewOauth2Secret}
                        saveLabel={mode === 'create' ? 'Create' : 'Save'}
                        disabled={busy}
                    />
                    <div className="space-y-2">
                        <Label>Client ID *</Label>
                        <Input
                            value={draft.auth.oauth2_client_id || ''}
                            onChange={(e) => setAuth({
                                oauth2_client_id: e.target.value
                            })}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Client secret {mode === 'edit' && existing?.auth?.oauth2_client_secret_ref ? '(leave empty to keep existing)' : '*'}</Label>
                        <Input
                            type="password"
                            value={newOauth2Secret}
                            onChange={(e) => setNewOauth2Secret(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Token URL *</Label>
                        <Input
                            value={draft.auth.oauth2_token_url || ''}
                            onChange={(e) => setAuth({
                                oauth2_token_url: e.target.value
                            })}
                            placeholder="https://oauth.example.com/token"
                        />
                    </div>
                </>
            )}

            <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button onClick={handleSave} disabled={busy}>
                    {busy ? 'Saving…' : (mode === 'create' ? 'Create' : 'Save')}
                </Button>
            </div>
        </div>
    );
};

export default MCPForm;
